import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Zap, MapPin, Calendar, Users, Check, Loader2, AlertTriangle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast, Toaster } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateLong(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function PublicEventPage() {
  const { publicId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState("");
  const [dateIdx, setDateIdx] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [voted, setVoted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/public/${publicId}`);
      setEvent(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Evento non trovato");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [publicId]);

  const submitVote = async () => {
    if (!name.trim()) return toast.error("Scrivi il tuo nome");
    if (dateIdx === null) return toast.error("Scegli una data");
    setSubmitting(true);
    try {
      await axios.post(`${API}/events/public/${publicId}/vote`, {
        name: name.trim(),
        date_index: dateIdx,
        note: note.trim(),
      });
      toast.success("Grazie per il tuo voto!");
      setVoted(true);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio voto");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F6]">
        <Loader2 className="h-10 w-10 animate-spin text-[#FF7C43]" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F6] p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-amber-500" />
          <h1 className="mt-4 font-display text-3xl font-bold text-[#2D2926]">
            Evento non trovato
          </h1>
          <p className="mt-2 text-[#5C5855]">{error}</p>
          <a href="/" className="mt-6 inline-block rounded-full bg-[#FF7C43] px-6 py-3 font-semibold text-white">
            Torna alla home
          </a>
        </div>
      </div>
    );
  }

  const tallies = event.date_options.map((_, i) => event.votes.filter((v) => v.date_index === i).length);

  return (
    <div className="min-h-screen bg-[#FBF9F6] pb-20">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "white", border: "1px solid #E8E1D9", color: "#2D2926",
            borderRadius: "16px", fontFamily: "Outfit, sans-serif",
          },
        }}
      />

      {/* Decorative blobs */}
      <div className="pointer-events-none fixed top-[-120px] right-[-120px] h-[420px] w-[420px] rounded-full bg-[#FF7C43]/15 blur-3xl" />
      <div className="pointer-events-none fixed bottom-[-160px] left-[-100px] h-[380px] w-[380px] rounded-full bg-[#C87941]/15 blur-3xl" />

      <div className="relative mx-auto max-w-2xl px-4 pt-10 sm:px-6">
        {/* Brand header */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#E8E1D9] bg-white/70 px-4 py-1.5 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF7C43] pulse-dot" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
              Cuor di Papaya · EASY Family
            </span>
          </div>
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-3xl papaya-gradient tuscany-glow float-slow">
            <Zap className="h-8 w-8 text-white" fill="white" />
          </div>
          <p className="mt-4 text-sm font-semibold text-[#5C5855]">
            Ti ha invitato <span className="text-[#FF7C43]">{event.creator_name}</span>
          </p>
        </div>

        {/* Event card */}
        <div className="rounded-3xl border border-[#E8E1D9] bg-white p-6 shadow-sm sm:p-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
            Ritrovo
          </span>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D2926]">
            {event.title}
          </h1>
          {event.description && (
            <p className="mt-3 text-base text-[#5C5855]">{event.description}</p>
          )}
          {event.location && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FBF9F6] px-3 py-1.5 text-sm font-medium text-[#5C5855]">
              <MapPin className="h-3.5 w-3.5 text-[#C87941]" />
              {event.location}
            </div>
          )}
        </div>

        {/* Confirmed banner */}
        {event.status === "confirmed" && event.final_date && (
          <div className="mt-6 rounded-3xl border-2 border-green-300 bg-green-50 p-6 text-center">
            <Check className="mx-auto h-10 w-10 text-green-600" />
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.3em] text-green-700">
              Data confermata
            </p>
            <p className="mt-1 font-display text-2xl font-bold text-green-900">
              {formatDateLong(event.final_date)}
            </p>
          </div>
        )}

        {/* Cancelled banner */}
        {event.status === "cancelled" && (
          <div className="mt-6 rounded-3xl border-2 border-stone-300 bg-stone-100 p-6 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-stone-500" />
            <p className="mt-2 font-display text-xl font-bold text-stone-700">
              Ritrovo annullato
            </p>
          </div>
        )}

        {/* Date options */}
        {event.status === "voting" && (
          <>
            <div className="mt-8 mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#FF7C43]" />
              <h2 className="font-display text-lg font-bold text-[#2D2926]">
                Quale data preferisci?
              </h2>
            </div>
            <div className="space-y-2">
              {event.date_options.map((d, i) => {
                const selected = dateIdx === i;
                const count = tallies[i];
                return (
                  <button
                    key={i}
                    data-testid={`public-date-${i}`}
                    onClick={() => !voted && setDateIdx(i)}
                    disabled={voted}
                    className={`flex w-full items-center justify-between gap-3 rounded-2xl border-2 p-4 text-left transition-all ${
                      selected
                        ? "border-[#FF7C43] bg-[#FF7C43]/5"
                        : "border-[#E8E1D9] bg-white hover:border-[#C87941]"
                    } ${voted ? "opacity-70" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          selected ? "papaya-gradient text-white" : "bg-[#FBF9F6] text-[#FF7C43]"
                        }`}>
                          {i + 1}
                        </span>
                        <p className="font-semibold text-[#2D2926]">{formatDateLong(d)}</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-[#FBF9F6] px-3 py-1 text-xs font-bold text-[#FF7C43]">
                      <Users className="h-3 w-3" /> {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Voting form */}
            {!voted ? (
              <div className="mt-6 space-y-3 rounded-3xl border border-[#E8E1D9] bg-white p-5 sm:p-6">
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                    Il tuo nome
                  </label>
                  <Input
                    data-testid="public-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Es. Zia Maria"
                    className="rounded-xl border-[#E8E1D9] bg-white"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                    Un commento (opzionale)
                  </label>
                  <Textarea
                    data-testid="public-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Es. Porto il tiramisù!"
                    rows={2}
                    className="rounded-xl border-[#E8E1D9] bg-white resize-none"
                  />
                </div>
                <Button
                  data-testid="public-vote-submit"
                  type="button"
                  onClick={submitVote}
                  disabled={submitting || dateIdx === null}
                  className="w-full rounded-full bg-red-600 py-6 text-base font-bold uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {submitting ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Zap className="mr-1 h-5 w-5" fill="white" />
                  )}
                  Vota
                </Button>
              </div>
            ) : (
              <div className="mt-6 rounded-3xl border-2 border-green-200 bg-green-50 p-6 text-center">
                <Check className="mx-auto h-10 w-10 text-green-600" />
                <p className="mt-2 font-display text-xl font-bold text-green-900">
                  Grazie {name}!
                </p>
                <p className="mt-1 text-sm text-green-800">
                  Il tuo voto è stato registrato. Ti faremo sapere la data confermata.
                </p>
              </div>
            )}
          </>
        )}

        <p className="mt-12 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-[#C87941]/60">
          Fatto con amore da ZapHQ.space
        </p>
      </div>
    </div>
  );
}
